import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_USERS } from '@/mocks/directories'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { Colleague } from '@/types/domain'

/* Who may be asked for a quote's price (S-2a) — the «Запросить расчёт» picker.
 *
 * GET /api/users/pricing-desk, guarded by `quote.team.assign` — the people who
 * ask are the people who pick. The answer is the `/users` Colleague shape (id,
 * name, role, office) narrowed by the SERVER to accounts that can sign in,
 * hold `quote.pricing.provide`, and reach quotes at `assigned` or `all` (the
 * superuser included). That rule reads permissions this client sees for nobody
 * but itself, so nothing is filtered here — useOperationsOwners' reasoning, and
 * the request re-checks the pick inside its transaction regardless (422
 * `assigneeId: not-assignable` / `not-pricing`).
 *
 * `role` is the role KEY; the picker names it with `useRoleLabel`, because a
 * custom tariff-desk role granted the capability is exactly who this list
 * exists to include.
 */
export function usePricingDesk(
  /** Fetched only while the request sheet is open. */
  { enabled }: { enabled?: boolean } = {},
) {
  return useQuery<Colleague[]>({
    queryKey: queryKeys.pricingDesk(),
    enabled,
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_PRICING_DESK
        : (await api.get<{ items: Colleague[] }>('/users/pricing-desk')).items,
    /* No staleTime, for useOperationsOwners' reason: the list answers a
       refusable question — may this person be asked NOW — and one read per
       opened sheet is cheap. */
  })
}

/* The fixture answer: the active seeded Pricing accounts, projected from the
   user fixtures rather than written out again. */
const MOCK_PRICING_DESK: Colleague[] = MOCK_USERS.filter(
  (user) => user.isActive && user.role === 'pricing',
).map((user) => ({
  id: user.id,
  name: user.name,
  role: user.role,
  office: user.office,
}))
